import { ReactNode } from 'react';
import { FieldValues, UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { FormFieldsSkeleton } from '@/components/core/skeletons/skeletons';
import { Button } from '@/components/ui-kit/button';
import { Card } from '@/components/ui-kit/card';
import { Form } from '@/components/ui-kit/form';

interface ApplyFormPageProps<TValues extends FieldValues> {
  /** Provided to the fields below, so `FormField`s work without each caller adding the provider. */
  form: UseFormReturn<TValues>;
  /** Existing UILM key for the form title (e.g. `BYOD_APPLICATION_FORM`). */
  titleKey: string;
  /** A banner shown above the form (deadline warnings, policy notes). */
  notice?: ReactNode;
  isSubmitting: boolean;
  submitDisabled?: boolean;
  /** Editing a saved application: the button reads "Update", as in the legacy forms. */
  isEdit?: boolean;
  onCancel: () => void;
  /** Wired to the form's `handleSubmit`; the fields are the children. */
  onSubmit: React.FormEventHandler<HTMLFormElement>;
  children: ReactNode;
  /** The values that fill the form (the saved application, or the applicant's profile) are loading:
   * placeholder fields instead of blank ones that fill in a moment later. */
  isLoading?: boolean;
}

/** Card, heading and Cancel / Submit footer shared by every service desk apply form. */
export const ApplyFormPage = <TValues extends FieldValues>({
  form,
  titleKey,
  notice,
  isSubmitting,
  submitDisabled,
  isEdit = false,
  onCancel,
  onSubmit,
  children,
  isLoading = false,
}: ApplyFormPageProps<TValues>) => {
  const { t } = useTranslation();

  return (
    <div className="flex w-full flex-col gap-4">
      <h1 className="text-lg font-semibold">{t(titleKey)}</h1>
      {notice}
      <Card className="p-4 sm:p-6">
        <Form {...form}>
          <form onSubmit={onSubmit} className="flex flex-col gap-6">
            {isLoading ? <FormFieldsSkeleton fields={8} columns={2} /> : children}
            <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <Button type="button" variant="outline" onClick={onCancel}>
                {t('CANCEL')}
              </Button>
              <Button
                type="submit"
                loading={isSubmitting}
                disabled={isSubmitting || submitDisabled || isLoading}
              >
                {t(isEdit ? 'UPDATE' : 'SUBMIT')}
              </Button>
            </div>
          </form>
        </Form>
      </Card>
    </div>
  );
};
